import React, { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  ErrorBoundary,
  navigationItems as allNavigationItems,
  APP_ROUTE_PREFIXES,
} from '@lucid/lego';
import { useStore } from '../../store/store';
import { useGetLabels } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';
import { isNavigationEnabled } from '../../config/featureFlags';
import { NAVIGATION_LABELS_ID } from '../../config/constants';
import styles from './LayoutWrapper.module.scss';

/** Environments where the region selector is visible in the Settings slider */
const REGION_SELECTOR_ENVS = ['development', 'stage'];

/** Environments where the Knowledge Center link is visible in the Settings slider */
const KNOWLEDGE_CENTER_ENVS = ['development', 'qa', 'stage'];

const LayoutWrapper: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const toggleTheme = useStore((state) => state.toggleTheme);
  const locale = useStore((state) => state.locale);
  const setLocale = useStore((state) => state.setLocale);
  const language = useStore((state) => state.language);
  const setLanguage = useStore((state) => state.setLanguage);
  const isSidebarCollapsed = useStore((state) => state.isSidebarCollapsed);
  const setSidebarCollapsed = useStore((state) => state.setSidebarCollapsed);
  const location = useLocation();
  const navigate = useNavigate();
  const lastRedirectPath = useRef<string | null>(null);

  // Filter navigation items based on enabled pages
  const navigationItems = useMemo(
    () => allNavigationItems.filter((item) => isNavigationEnabled(item.id)),
    []
  );

  // Fetch navigation labels from Contentful
  const { data: navigationLabels } = useGetLabels({
    id: NAVIGATION_LABELS_ID,
    locale: convertToContentfulLocale(locale),
    language,
  });

  // Handle language change
  const handleLanguageChange = useCallback(
    (languageCode: string | null) => {
      setLanguage(languageCode);
    },
    [setLanguage]
  );

  // Sync store locale and language with URL locale on mount and path changes
  useEffect(() => {
    const currentPath = location.pathname;
    // Match locale at start of path, with or without trailing slash
    const localeMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})(?:\/|$)/i);

    if (localeMatch) {
      const urlLocale = localeMatch[1].toLowerCase();
      if (urlLocale !== locale) {
        setLocale(urlLocale);
        // Sync language: auto-select English for non-English locales
        const langCode = urlLocale.split('-')[0]?.toLowerCase();
        setLanguage(langCode !== 'en' ? 'en' : null);
      }
    } else if (locale && locale !== 'en-us') {
      // Store has a non-English locale but URL has no prefix — add it
      const targetPath = `/${locale}${currentPath}`;
      // Guard against infinite redirects by tracking the last redirect target
      if (lastRedirectPath.current !== targetPath) {
        lastRedirectPath.current = targetPath;
        navigate(targetPath, { replace: true });
      }
    }
  }, [location.pathname, locale, setLocale, setLanguage, navigate]);

  const showRoleDropdown = import.meta.env.VITE_SHOW_ROLE_DROPDOWN === 'true';

  const handleLocaleChange = (newLocale: string) => {
    // Update store first
    setLocale(newLocale);

    // Auto-select English for non-English locales, reset for English locales
    const newLangCode = newLocale.split('-')[0]?.toLowerCase();
    setLanguage(newLangCode !== 'en' ? 'en' : null);

    // Navigate to the new locale URL using React Router
    const currentPath = location.pathname;
    const currentLocaleMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})(\/.*)?$/i);

    let newPath: string;
    if (currentLocaleMatch) {
      // There's an existing locale in the path
      const pathWithoutLocale = currentLocaleMatch[2] || '/';

      if (newLocale === 'en-us') {
        // For en-us, remove locale prefix
        newPath = pathWithoutLocale;
      } else {
        // For non-en-us, add locale prefix
        newPath = `/${newLocale}${pathWithoutLocale}`;
      }
      navigate(newPath, { replace: true });
    }
  };

  // Only show the region selector on local/dev and stage — undefined hides it in Layout
  const onLocaleChange = REGION_SELECTOR_ENVS.includes(import.meta.env.VITE_ENV)
    ? handleLocaleChange
    : undefined;

  const handleError = (error: Error) => {
    console.error('Route Error:', error);
  };

  const showKnowledgeCenter = KNOWLEDGE_CENTER_ENVS.includes(import.meta.env.VITE_ENV);

  // The docs-mfe (Docusaurus) is always served at /knowledge-center/.
  // In development, docs-mfe runs on a different port so VITE_KNOWLEDGE_CENTER_BASE_URL
  // provides the full origin (e.g. http://localhost:3003). In qa/stage it's the same origin.
  const knowledgeCenterBase = import.meta.env.VITE_KNOWLEDGE_CENTER_BASE_URL ?? '';
  const knowledgeCenterHref = `${knowledgeCenterBase}/knowledge-center/`;

  return (
    <Layout
      theme={theme}
      onToggleTheme={toggleTheme}
      showRoleDropdown={showRoleDropdown}
      currentPath={location.pathname}
      locale={locale}
      onLocaleChange={onLocaleChange}
      selectedLanguage={language}
      onLanguageChange={handleLanguageChange}
      isSidebarCollapsed={isSidebarCollapsed}
      onSidebarCollapseChange={setSidebarCollapsed}
      sidebarLabels={navigationLabels}
      navigationItems={navigationItems}
      onNavigate={navigate}
      ownRoutePrefixes={APP_ROUTE_PREFIXES.partnerPortal}
      knowledgeCenterLink={
        showKnowledgeCenter ? { href: knowledgeCenterHref, label: 'Knowledge Center' } : undefined
      }
    >
      <ErrorBoundary theme={theme} onError={handleError} showDetails={import.meta.env.DEV}>
        <Suspense fallback={<div className={styles.suspenseFallback} />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
};

export default LayoutWrapper;
